import classNames from 'classnames'
import { FC, useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { useDisableScroll } from 'src/hooks/useDisableScroll'
import { ContextTagType } from 'src/services/aiListeningApi/brandProfile.types'
import styles from './prompts.module.scss'
import { CONTEXT_TAG_LABELS, CONTEXT_TAG_TYPES } from '../shared/utils'

interface FilterModalProps {
  isOpen: boolean
  onClose: () => void
  onSave: (types: ContextTagType[]) => void
  // [] = every context type.
  value: ContextTagType[]
}

const CheckIcon: FC = () => (
  <svg fill="none" height="12" viewBox="0 0 12 12" width="12">
    <path
      d="M2.5 6.2L4.8 8.5L9.5 3.5"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.6"
    />
  </svg>
)

// Filter prompts by the type of context detected on them.
export const FilterModal: FC<FilterModalProps> = ({
  isOpen,
  onClose,
  onSave,
  value,
}) => {
  const [selected, setSelected] = useState<ContextTagType[]>(value)
  const [isListOpen, setIsListOpen] = useState(true)

  useEffect(() => {
    if (isOpen) {
      setSelected(value.length ? value : [...CONTEXT_TAG_TYPES])
      setIsListOpen(true)
    }
  }, [isOpen])

  useDisableScroll(isOpen)

  if (!isOpen) return null

  const isAll = selected.length === CONTEXT_TAG_TYPES.length

  const toggle = (type: ContextTagType) =>
    setSelected((previous) =>
      previous.includes(type)
        ? previous.filter((item) => item !== type)
        : CONTEXT_TAG_TYPES.filter(
            (item) => item === type || previous.includes(item)
          )
    )

  const summary = isAll ? (
    <>
      <span className={styles.filterAccent}>All</span> Detected Context Selected
    </>
  ) : (
    <>
      <span className={styles.filterAccent}>{selected.length}</span> Selected
    </>
  )

  return createPortal(
    <div className={styles.overlay} onClick={onClose}>
      <div
        aria-labelledby="prompt-filter-title"
        aria-modal="true"
        className={styles.filterModal}
        onClick={(event) => event.stopPropagation()}
        role="dialog"
      >
        <div className={styles.filterHeader}>
          <h2 className={styles.filterTitle} id="prompt-filter-title">
            Filter
          </h2>
          <button
            aria-label="Close"
            className={styles.filterClose}
            onClick={onClose}
            type="button"
          >
            <svg fill="none" height="24" viewBox="0 0 24 24" width="24">
              <path
                d="M18 6L6 18M6 6L18 18"
                stroke="currentColor"
                strokeLinecap="round"
                strokeWidth="2"
              />
            </svg>
          </button>
        </div>

        <div className={styles.filterBox}>
          <button
            aria-expanded={isListOpen}
            className={styles.filterBoxHeader}
            onClick={() => setIsListOpen((open) => !open)}
            type="button"
          >
            <span className={styles.filterBoxLabel}>Detected Context</span>
            <span className={styles.filterSummary}>{summary}</span>
            <svg
              className={classNames(styles.filterChevron, {
                [styles.filterChevronOpen]: isListOpen,
              })}
              fill="none"
              height="20"
              viewBox="0 0 20 20"
              width="20"
            >
              <path
                d="M5 12.5L10 7.5L15 12.5"
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="1.8"
              />
            </svg>
          </button>
          {isListOpen && (
            <div className={styles.filterList}>
              <button
                className={styles.filterOption}
                onClick={() => setSelected(isAll ? [] : [...CONTEXT_TAG_TYPES])}
                type="button"
              >
                <span
                  className={classNames(styles.filterCheck, {
                    [styles.filterCheckStrong]: isAll,
                  })}
                >
                  {isAll && <CheckIcon />}
                </span>
                All Detected Context
              </button>
              {CONTEXT_TAG_TYPES.map((type) => (
                <button
                  className={styles.filterOption}
                  key={type}
                  onClick={() => toggle(type)}
                  type="button"
                >
                  <span
                    className={classNames(styles.filterCheck, {
                      [styles.filterCheckOn]: selected.includes(type),
                    })}
                  >
                    {selected.includes(type) && <CheckIcon />}
                  </span>
                  {CONTEXT_TAG_LABELS[type]}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className={styles.filterFooter}>
          <button
            className={styles.resetButton}
            onClick={() => setSelected([...CONTEXT_TAG_TYPES])}
            type="button"
          >
            <svg fill="none" height="16" viewBox="0 0 16 16" width="16">
              <path
                d="M3 8a5 5 0 1 0 1.5-3.5M3 2.5v2.5h2.5"
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="1.4"
              />
            </svg>
            Reset Filter
          </button>
          <div className={styles.filterActions}>
            <button
              className={styles.cancelButton}
              onClick={onClose}
              type="button"
            >
              Cancel
            </button>
            <button
              className={styles.saveButton}
              disabled={selected.length === 0}
              onClick={() => onSave(isAll ? [] : selected)}
              type="button"
            >
              Save
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  )
}
